import {
    Stage,
    Layer,
    Text,
    Image as KonvaImage,
    Rect,
    Arrow
} from "react-konva";
import { useEffect, useRef, useState, Fragment } from "react";
import VariablePopup from "./VariablePopup";
import { _axios } from "../../utils/axios";
import Select from "../../components/CommonComponents/Select/Select";

interface Shape {
    id?: number;
    name?: string;
    image_path: string;
    svg_template?: string;
    category?: string;
}

interface TextItem {
    text?: string;
    x: number;
    y: number;
    rotation?: number;
    fontSize?: number;
    arrow?: {
        from: { x: number; y: number };
        to: { x: number; y: number };
    };
    type?: "TEXT" | "ARROW" | "DIRECTION";
    image?: string;
}

interface CanvasEditorProps {
    shape: Shape;
    direction?: Shape | null;
    calculation: any;
    onExport: (data: ExportData) => void;
    initialData?: TextItem[];
    isEditMode?: boolean;
    isFinalSaved?: boolean;
    isNetAreaManual?: boolean;
}

interface ExportData {
    image: string;
    canvasData: any[];
    netArea: number;
    inputValues: Record<string, number>;
    calculationPosition: { x: number; y: number };
    shapeId?: number;
    templateImage?: string;
    shapeName?: string;
    shapeType?: string;
    directionName?: string;
}

const CanvasEditor: React.FC<CanvasEditorProps> = ({
    shape,
    direction,
    calculation,
    onExport,
    initialData,
    isFinalSaved
}) => {
    const [texts, setTexts] = useState<TextItem[]>([]);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const stageRef = useRef<any>(null);
    const [selectedText, setSelectedText] = useState<number | null>(null);
    const [textValue, setTextValue] = useState("");
    const [showVariables, setShowVariables] = useState(false);
    const [variables, setVariables] = useState<string[]>([]);
    const [arrowDirection, setArrowDirection] = useState<"left" | "right" | "top" | "bottom" | "diagonal">("left");
    const [arrowLength, setArrowLength] = useState(50);
    const [showArrow, setShowArrow] = useState(true);
    const [isAddingArrow, setIsAddingArrow] = useState(false);
    const [selectedArrow, setSelectedArrow] = useState<number | null>(null);
    const [formula, setFormula] = useState<string>("");
    const [formulaVariables, setFormulaVariables] = useState<string[]>([]);
    const [inputValues, setInputValues] = useState<Record<string, number>>({});
    const [calculationPosition, setCalculationPosition] = useState({
        x: 40,
        y: 20
    });
    const [isExporting, setIsExporting] = useState(false);
    const canvasWidth = 1400;
    const canvasHeight =
        Math.max(700, 500 + formulaVariables.length * 40);
    const imageCache = useRef<Record<string, HTMLImageElement>>({});
    const [fontSize, setFontSize] = useState(22);
    const [icon1, setIcon1] = useState<HTMLImageElement | null>(null);
    const [icon2, setIcon2] = useState<HTMLImageElement | null>(null);
    const line1Ref = useRef<any>(null);
    const line2Ref = useRef<any>(null);

    useEffect(() => {
        const img1 = new window.Image();
        img1.crossOrigin = "anonymous";
        img1.src = "https://gunthewariqa.blob.core.windows.net/cp-bpms-qa/schematic_plan_shapes/Plot_Boundry_Shown_By.svg";
        img1.onload = () => setIcon1(img1);

        const img2 = new window.Image();
        img2.crossOrigin = "anonymous";
        img2.src = "https://gunthewariqa.blob.core.windows.net/cp-bpms-qa/schematic_plan_shapes/Area_Surrendered_To_Nagar_Panchayat_For_Road_Widening.svg";
        img2.onload = () => setIcon2(img2);
    }, []);

    useEffect(() => {
        if (!direction) return;

        setTexts(prev => {
            const filtered = prev.filter(t => t.type !== "DIRECTION");

            return [
                ...filtered,
                {
                    x: canvasWidth - 120,
                    y: 50,
                    type: "DIRECTION",
                    image: direction.image_path
                }
            ];
        });
    }, [direction]);

    useEffect(() => {
        if (direction !== null) return;

        setTexts(prev => prev.filter(t => t.type !== "DIRECTION"));
    }, [direction]);

    useEffect(() => {
        if (initialData?.length) {
            setTexts(initialData);

            // Restore input values in edit mode
            if (calculation?.inputValues) {
                setInputValues(calculation.inputValues);
            }
        } else {
            setTexts([]);
        }

        setSelectedText(null);
    }, [initialData]);

    useEffect(() => {
        if (calculation?.inputValues) {
            setInputValues(calculation.inputValues);
        }
    }, [calculation]);

    useEffect(() => {
        if (calculation?.calculationPosition) {
            setCalculationPosition(calculation.calculationPosition);
        }
    }, [calculation]);

    useEffect(() => {
        if (isFinalSaved) return;

        if (!formula || !inputValues || Object.keys(inputValues).length === 0) return;

        const calculated = evaluateFormula(formula, inputValues);

        console.log("Exporting texts:", texts);
        const timer = setTimeout(() => {
            if (stageRef.current) {
                console.log("Exporting texts:", texts);
                onExport({
                    image: stageRef.current.toDataURL({
                        pixelRatio: 4,
                        width: stageRef.current.width(),
                        height: stageRef.current.height()
                    }),
                    canvasData: texts,
                    netArea: calculated,
                    inputValues,
                    calculationPosition,
                    shapeId: shape?.id,
                    templateImage: shape?.image_path,
                    shapeName: shape?.name,
                    shapeType: shape?.category,
                    directionName: direction?.name
                });
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [
        inputValues,
        formula,
        texts,
        isFinalSaved,
        calculation?.plotArea,
        calculation?.kPartArea,
        calculation?.roadArea,
        calculation?.netArea,
        calculationPosition,
        direction?.name,
        shape?.id,
        shape?.name,
        shape?.category
    ]);

    useEffect(() => {
        const isEdit = initialData && initialData.length > 0;

        if (!isEdit) {
            setTexts([]);
        }
        setSelectedText(null);

        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.src = shape.image_path;

        img.onload = () => setImage(img);

        const loadVariables = async () => {
            if (!shape?.id) return;

            const res = await _axios.get(`/schematic/shape/${shape.id}`);
            console.log("API RESPONSE", res.data);
            setFormula(res.data.formula);
            setFormulaVariables(res.data.variables || []);

            if (res.data.variables?.length) {
                const uniqueVars = Array.from(new Set<string>(res.data.variables));
                const cleanedVars = uniqueVars.filter(v => v.length > 1);

                setVariables(cleanedVars);

                if (!inputValues || Object.keys(inputValues).length === 0) {
                    setShowVariables(true);
                }
            }
        };

        loadVariables();
    }, [shape]);

    const addText = () => {
        if (!textValue.trim()) return;

        const baseX = 750;
        const baseY = 100;

        const index = texts.length;

        setTexts(prev => [
            ...prev,
            {
                text: textValue,
                x: baseX + (index % 2) * 150,   // 2 column layout
                y: baseY + Math.floor(index / 2) * 50,
                fontSize: fontSize
            }
        ]);

        setTextValue("");
    };

    const addArrow = () => {
        const x = 250;
        const y = 250;

        setTexts(prev => [
            ...prev,
            {
                x,
                y,
                arrow: getArrow(x, y)
            }
        ]);
    };

    const handleExport = () => {
        if (!stageRef.current) return;

        setIsExporting(true);

        setTimeout(() => {
            const uri = stageRef.current.toDataURL({
                pixelRatio: 4,
                width: stageRef.current.width(),
                height: stageRef.current.height()
            });

            const calculatedNetArea = evaluateFormula(formula, inputValues);

            onExport({
                image: uri,
                canvasData: texts,
                netArea: calculatedNetArea,
                inputValues,
                calculationPosition,
                shapeId: shape?.id,
                templateImage: shape?.image_path,
                shapeName: shape?.name,
                shapeType: shape?.category,
                directionName: direction?.name
            });
            setIsExporting(false); // bring back helpers
        }, 50);
    };

    const getArrow = (x: number, y: number) => {
        const length = 80;

        switch (arrowDirection) {
            case "left":
                return { from: { x, y }, to: { x: x - length, y } };

            case "right":
                return { from: { x, y }, to: { x: x + length, y } };

            case "top":
                return { from: { x, y }, to: { x, y: y - length } };

            case "bottom":
                return { from: { x, y }, to: { x, y: y + length } };

            case "diagonal":
                return {
                    from: { x, y },
                    to: { x: x + length, y: y - length }
                };
        }
    };

    const evaluateFormula = (formula: string, values: Record<string, number>) => {
        try {
            if (!formula) return 0;

            let exp = formula;

            Object.entries(values).forEach(([key, val]) => {
                exp = exp.replaceAll(key, String(val));
            });
            console.log("Final Expression:", exp);

            // SAFETY CHECK
            if (!exp.includes("(") && !exp.includes(")")) {
                console.warn("Invalid formula:", exp);
            }

            return Function(`return (${exp})`)(); // wrap in ()
        } catch (err) {
            console.error("Formula error", err, formula);
            return 0;
        }
    };

    const isValidFormula = (formula: string) => {
        try {
            new Function(`return (${formula})`);
            return true;
        } catch {
            return false;
        }
    };

    const calculatedNetArea = isValidFormula(formula)
        ? evaluateFormula(formula, inputValues)
        : 0;

    const netArea = calculation?.netArea ?? calculatedNetArea;

    const formatValue = (val: number) =>
        val ? String(val) : "0";

    //     const dimensionText = `
    // DIMENSIONS OF PLOT IN METERS:

    // ${formulaVariables
    //             .map((key) => `${key} = ${formatValue(inputValues[key] || 0)} Mt.`)
    //             .join("\n")}
    // `;

    //     const fullText = `Reference :-

    // 1) Plot Boundary Shown Thus
    // 2) Area Surrendered to C.C.M.C


    // DIMENSIONS OF PLOT IN METERS:

    // ${formulaVariables
    //             .map((key) => `${key} = ${formatValue(inputValues[key] || 0)} Mt.`)
    //             .join("\n")}


    // PLOT AREA AS PER SALEDEED: ${calculation.plotArea} Sq. Mt.
    // DEDUCT AREA AS PER K PART: ${calculation.kPartArea || 0} Sq. Mt.
    // DEDUCT AREA UNDER ROAD WIDENING: ${calculation.roadArea} Sq. Mt.
    // NET AREA REGULARIZED: ${netArea} Sq. Mt.
    // `;

    const baseX = (calculationPosition.x || 20) + 30;
    let y = (calculationPosition.y || 20) + 20;

    const refHeadingY = y;

    y += 25;
    const refContentY = y;

    y += 100;
    const dimHeadingY = y;

    y += 25;
    const dimValuesY = y;

    y += formulaVariables.length * 40 + 60;
    const finalCalcY = y;

    texts.forEach((t) => {
        if (t.type === "DIRECTION" && t.image) {
            if (!imageCache.current[t.image]) {
                const img = new window.Image();
                img.crossOrigin = "anonymous";
                img.src = t.image;
                imageCache.current[t.image] = img;
            }
        }
    });

    return (
        <div className="space-y-4">

            {/* ================= VARIABLE POPUP ================= */}
            {showVariables && (
                <VariablePopup
                    variables={variables}
                    defaultValues={inputValues}
                    onSave={(values) => {
                        setShowVariables(false);
                        setInputValues(values);

                        let updatedTexts: TextItem[] = [];

                        setTexts(prev => {
                            const updated = [...prev];

                            formulaVariables.forEach((key, index) => {
                                const val = values[key] || 0;

                                if (updated[index]) {
                                    updated[index] = {
                                        ...updated[index],
                                        text: `${val}`
                                    };
                                } else {
                                    updated.push({
                                        text: `${val}`,
                                        x: 750 + (index % 2) * 120,
                                        y: 120 + Math.floor(index / 2) * 40
                                    });
                                }
                            });

                            updatedTexts = updated; // capture latest
                            return updated;
                        });

                        const calculatedNetArea = evaluateFormula(formula, values);

                        setTimeout(() => {
                            if (stageRef.current) {
                                const uri = stageRef.current.toDataURL({
                                    pixelRatio: 4,
                                    width: stageRef.current.width(),
                                    height: stageRef.current.height()
                                });

                                onExport({
                                    image: uri,
                                    canvasData: updatedTexts,
                                    netArea,
                                    inputValues: values,
                                    calculationPosition,
                                    shapeId: shape?.id,
                                    templateImage: shape?.image_path,
                                    shapeName: shape?.name,
                                    shapeType: shape?.category,
                                    directionName: direction?.name
                                });
                            }
                        }, 100);
                    }}
                    onBack={() => setShowVariables(false)}
                />
            )}

            {/* ================= CONTROLS ================= */}
            <div className="space-y-2">

                {/* Row 1 */}
                <div className="flex flex-wrap gap-2">
                    <button
                        className="bg-primary px-5 py-2 grow md:grow-0 rounded font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={addText}
                    >
                        Add Text
                    </button>

                    <input
                        value={textValue}
                        onChange={(e) => setTextValue(e.target.value)}
                        className="border rounded-md px-2 py-1"
                        placeholder="Enter Text"
                    />

                    <button
                        className="bg-[#7CE361] text-[#1A7C02] px-3 py-1 border rounded-md"
                        onClick={() => {
                            if (selectedText === null) return;

                            const updated = [...texts];

                            updated[selectedText].text =
                                textValue || updated[selectedText].text;

                            updated[selectedText].arrow = getArrow(
                                updated[selectedText].x,
                                updated[selectedText].y
                            );

                            setTexts(updated);
                        }}
                    >
                        ✓
                    </button>

                    <button
                        className="bg-[#FB9393] text-[#FF0202] px-3 py-1 border rounded-md"
                        onClick={() => {
                            if (selectedText === null) return;
                            setTexts(texts.filter((_, i) => i !== selectedText));
                            setSelectedText(null);
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Row 2 → Arrow Controls */}
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        className="bg-primary px-5 py-2 grow md:grow-0 rounded font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={() => setIsAddingArrow(true)}
                    >
                        Add Arrow
                    </button>
                    <button
                        className="bg-primary px-5 py-2 grow md:grow-0 rounded font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={() => {
                            if (selectedArrow === null) return;

                            const item = texts[selectedArrow];

                            if (!item?.arrow) return;

                            setTexts(texts.filter((_, i) => i !== selectedArrow));
                            setSelectedArrow(null);
                        }}
                    >
                        Delete Arrow
                    </button>

                    <label className="mb-0 bg-primary px-4 py-2 grow md:grow-0 rounded-md font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 cursor-pointer items-center">
                        <input
                            type="checkbox"
                            className="w-auto mr-4 shrink-0 basis-[15px]"
                            checked={showArrow}
                            onChange={() => setShowArrow(!showArrow)}
                        />
                        Arrow
                    </label>

                    <Select
                        className="border rounded-md px-2 py-2"
                        value={arrowDirection}
                        onChange={(e) => setArrowDirection(e.target.value as any)}
                    >
                        <option value="left">← Left</option>
                        <option value="right">→ Right</option>
                        <option value="top">↑ Top</option>
                        <option value="bottom">↓ Bottom</option>
                        <option value="diagonal">↗ Diagonal</option>
                    </Select>

                    <button
                        className="bg-primary px-5 py-2 rounded font-bold text-white"
                        onClick={() => setShowVariables(true)}
                    >
                        Edit Variables
                    </button>

                    <div className="flex items-center gap-2">
                        <label className="text-sm font-bold">
                            Font Size
                        </label>

                        <input
                            type="number"
                            value={fontSize}
                            onChange={(e) => setFontSize(Number(e.target.value))}
                            style={{ MozAppearance: "textfield" }}
                            className="border rounded-md px-2 py-1 w-20 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="Size"
                        />
                    </div>
                </div>
            </div>

            {/* ================= EDITOR ================= */}
            <div className="border rounded-md p-3 overflow-auto" style={{ maxHeight: "600px" }}>
                <h3 className="text-[#423F3F] font-semibold mb-2">Canvas Editor</h3>
                <div className="w-full overflow-auto" style={{ maxWidth: "100%" }}>
                    <Stage
                        style={{ cursor: isAddingArrow ? "crosshair" : "default" }}
                        width={canvasWidth}
                        height={canvasHeight}
                        ref={stageRef}
                        onClick={(e) => {
                            if (e.target === e.target.getStage()) {
                                setSelectedArrow(null);
                                setSelectedText(null);
                            }

                            if (!isAddingArrow) return;

                            const stage = e.target.getStage();
                            if (!stage) return;

                            const pointer = stage.getPointerPosition();
                            if (!pointer) return;

                            const { x, y } = pointer;

                            setTexts(prev => [
                                ...prev,
                                {
                                    x,
                                    y,
                                    arrow: getArrow(x, y)
                                }
                            ]);

                            setIsAddingArrow(false);
                        }}
                        onTap={(e) => {
                            if (e.target === e.target.getStage()) {
                                setSelectedArrow(null);
                                setSelectedText(null);
                            }

                            if (!isAddingArrow) return;

                            const stage = e.target.getStage();
                            if (!stage) return;

                            const pointer = stage.getPointerPosition();
                            if (!pointer) return;

                            const { x, y } = pointer;

                            setTexts(prev => [
                                ...prev,
                                {
                                    x,
                                    y,
                                    arrow: getArrow(x, y)
                                }
                            ]);

                            setIsAddingArrow(false);
                        }}
                    >
                        <Layer>
                            <Rect width={canvasWidth} height={canvasHeight} fill="white" />

                            {image && (
                                <KonvaImage
                                    image={image}
                                    x={700}
                                    y={50}
                                    width={600}
                                    height={500}
                                />
                            )}

                            {texts.map((t, i) => (
                                <Fragment key={i}>

                                    {/* Arrow */}
                                    {showArrow && t.arrow && (
                                        <>
                                            <Arrow
                                                points={[
                                                    t.arrow.from.x,
                                                    t.arrow.from.y,
                                                    t.arrow.to.x,
                                                    t.arrow.to.y
                                                ]}
                                                pointerLength={20}
                                                pointerWidth={20}
                                                fill="black"
                                                stroke={selectedArrow === i ? "red" : "black"}
                                                strokeWidth={selectedArrow === i ? 4 : 2}
                                                hitStrokeWidth={30}
                                                onClick={(e) => {
                                                    e.cancelBubble = true;

                                                    setSelectedArrow(prev => (prev === i ? null : i));
                                                }}
                                                onTap={(e) => {
                                                    e.cancelBubble = true;

                                                    setSelectedArrow(prev => (prev === i ? null : i));
                                                }}
                                                onMouseEnter={(e) => {
                                                    const container = e.target.getStage()?.container();
                                                    if (container) container.style.cursor = "pointer";
                                                }}
                                                onMouseLeave={(e) => {
                                                    const container = e.target.getStage()?.container();
                                                    if (container) container.style.cursor = "default";
                                                }}
                                            />

                                            {/* Draggable END POINT */}
                                            {!isExporting && !isFinalSaved && (
                                                <Rect
                                                    x={t.arrow.to.x - 5}
                                                    y={t.arrow.to.y - 5}
                                                    width={10}
                                                    height={10}
                                                    fill="red"
                                                    draggable
                                                    onDragMove={(e) => {
                                                        const updated = [...texts];

                                                        updated[i].arrow = {
                                                            ...t.arrow!,
                                                            to: {
                                                                x: e.target.x() + 5,
                                                                y: e.target.y() + 5
                                                            }
                                                        };

                                                        setTexts(updated);
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}

                                    {/* Text */}
                                    {t.text && (
                                        <Text
                                            text={t.text}
                                            x={t.x}
                                            y={t.y}
                                            fontSize={t.fontSize || 22}
                                            draggable
                                            rotation={t.rotation || 0}
                                            onClick={() => {
                                                setSelectedText(i);
                                                setTextValue(t.text || "");
                                            }}
                                            onTap={() => {
                                                setSelectedText(i);
                                                setTextValue(t.text || "");
                                            }}
                                            onDragEnd={(e) => {
                                                const newX = e.target.x();
                                                const newY = e.target.y();

                                                const updated = [...texts];

                                                const oldX = updated[i].x;
                                                const oldY = updated[i].y;

                                                const dx = newX - oldX;
                                                const dy = newY - oldY;

                                                updated[i].x = newX;
                                                updated[i].y = newY;

                                                const arrow = updated[i].arrow;

                                                if (arrow) {
                                                    updated[i].arrow = {
                                                        from: {
                                                            x: arrow.from.x + dx,
                                                            y: arrow.from.y + dy
                                                        },
                                                        to: {
                                                            x: arrow.to.x + dx,
                                                            y: arrow.to.y + dy
                                                        }
                                                    };
                                                }

                                                setTexts(updated);
                                            }}
                                        />
                                    )}
                                    {t.type === "DIRECTION" && t.image && (
                                        <KonvaImage
                                            image={imageCache.current[t.image]}
                                            x={t.x}
                                            y={t.y}
                                            width={100}
                                            height={100}
                                            draggable={!isExporting}
                                            onDragEnd={(e) => {
                                                const updated = [...texts];
                                                updated[i].x = e.target.x();
                                                updated[i].y = e.target.y();
                                                setTexts(updated);
                                            }}
                                        />
                                    )}
                                </Fragment>
                            ))}
                            <Rect
                                x={650}
                                y={0}
                                width={2}
                                height={canvasHeight}
                                fill="black"
                            />
                            {/* SINGLE calculation block */}
                            <Text
                                text="Reference :-"
                                x={baseX}
                                y={refHeadingY}
                                fontSize={22}
                                fontStyle="bold"
                            />

                            {/* LINE 1 */}
                            <Text
                                ref={line1Ref}
                                text="1) Plot Boundary Shown Thus"
                                x={baseX}
                                y={refContentY}
                                fontSize={20}
                            />

                            {icon1 && line1Ref.current && (
                                <KonvaImage
                                    image={icon1}
                                    x={baseX + line1Ref.current.width() + 10}
                                    y={refContentY - 40}
                                    width={100}
                                    height={100}
                                />
                            )}

                            {/* LINE 2 */}
                            <Text
                                ref={line2Ref}
                                text="2) Area Surrendered to Nagar Panchayat 
for Road Widening"
                                x={baseX}
                                y={refContentY + 35}
                                fontSize={20}
                            />

                            {icon2 && line2Ref.current && (
                                <KonvaImage
                                    image={icon2}
                                    x={baseX + line2Ref.current.width() + 10}
                                    y={refContentY + 35 - 40}
                                    width={100}
                                    height={100}
                                />
                            )}

                            <Text
                                text="DIMENSIONS OF PLOT IN METERS:"
                                x={baseX}
                                y={dimHeadingY}
                                fontSize={22}
                                fontStyle="bold"
                            />

                            <Text
                                text={formulaVariables
                                    .map((key) => `${key} = ${formatValue(inputValues[key] || 0)} Mt.`)
                                    .join("\n")}
                                x={baseX}
                                y={dimValuesY}
                                fontSize={20}
                                lineHeight={1.8}
                            />

                            <Text
                                text={`PLOT AREA AS PER SALE DEED / 7/12 = ${calculation.plotArea} Sq. Mt.
DEDUCT AREA FOR ___M TANGENT = ${calculation.kPartArea || 0} Sq. Mt.
DEDUCT AREA UNDER ROAD WIDENING = ${calculation.roadArea} Sq. Mt.
NET AREA REGULARIZED = ${netArea} Sq. Mt.`}
                                x={baseX}
                                y={finalCalcY}
                                fontSize={20}
                                lineHeight={1.8}
                            />
                        </Layer>
                    </Stage>

                    <button
                        className="bg-[#E9B925] text-white px-3 py-1 mt-2 border rounded-md"
                        onClick={() => {
                            if (selectedText === null) return;

                            const updated = [...texts];
                            updated[selectedText].rotation =
                                (updated[selectedText].rotation || 0) + 90;

                            setTexts(updated);
                        }}
                    >
                        Rotate Variables
                    </button>
                </div>
            </div>

            {/* ================= EXPORT ================= */}
            <div className="mt-4 text-center">
                <button
                    className="bg-[#E9B925] text-white px-6 py-2 rounded"
                    onClick={handleExport}
                >
                    Generate Schematic
                </button>
            </div>
        </div>
    );
};

export default CanvasEditor;
